import { IDisplaySlide, SlideType } from '@pcp/core/interfaces';
import { BackgroundFit } from 'shared/util-yjs-fabric';
import { LOGICAL_CANVAS_HEIGHT, LOGICAL_CANVAS_WIDTH } from 'shared/fabric-drawing/tools';
import { ScrollableViewport, ScrollDirection, ViewportSize } from './scrollable-viewport';
import { CoordinateSpace } from './slide-capability';

/**
 * Background strategies (`canvas-renderer-composition`, Decisions C9). Exactly ONE per slide.
 *
 * Each strategy owns the per-type background shape that was forked across `scrollDescriptorFor` /
 * `backgroundFitFor` / `hotspotScale` / `exportDimensions` in the monolithic component: the image
 * fit, the coordinate space (viewport + content-to-logical scale, Decision C9), the scroll model
 * (over the shared `ScrollableViewport`), and the export world size. The host feeds the host-cell
 * size and reads everything else from the strategy — so a variable bookpage viewport
 * (`canvas-bookpage-slides-viewport`) is a change to `BookpageBackground` alone, not the host.
 */
export interface SlideBackground {
    /** Image fit passed to `setBackgroundLocal` (constant per strategy). */
    readonly fit: BackgroundFit;
    /** Logical render space for the given host-cell size (Decision C9). */
    coordinateSpace(hostSize: ViewportSize): CoordinateSpace;
    /** Scroll model for the given host-cell size, or null when the slide doesn't scroll. */
    scrollModel(hostSize: ViewportSize): ScrollableViewport | null;
    /** Export world size — objects live in this space, so strokes beyond the visible window aren't clipped. */
    exportSize(hostSize: ViewportSize): ViewportSize;
    /** Master-export policy for the favorites flow (`canvas-slide-favorites` D3). */
    exportPolicy(): SlideExportPolicy;
}

/** How a slide's favorite master is scaled and how its ladder variants are produced. */
export interface SlideExportPolicy {
    /** Output scale relative to {@link SlideBackground.exportSize}'s logical space. */
    multiplier: number;
    /**
     * `true` → `low`/`med`/`high` are all the master itself (one size — the panorama contract,
     * matching the content pipeline where panorama variants share one native size).
     * `false` → `med`/`low` are downscales of the master (16:9 ladder).
     *
     * Also decides the **upload shape**: `buildFavoriteImageSet` omits `med`/`low` entirely for a
     * uniform ladder, so the master is uploaded ONCE and the service stores those bytes under the
     * absent names too — three identical *stored* variants, one uploaded part
     * (`panorama-single-part-upload`).
     */
    uniformLadder: boolean;
}

/**
 * Master export scale for FIXED 16:9 slides (`canvas-slide-favorites` D3): ×3 on the logical
 * 1280×720 puts the master at exactly the stored `high` contract (3840×2160); backgrounds below
 * 4K upscale exactly as the legacy server render did, vector content stays crisp.
 */
export const FIXED_MASTER_MULTIPLIER = 3;

/** Logical canvas size — the fixed viewport for every non-bookpage slide. */
const LOGICAL_SIZE: ViewportSize = { width: LOGICAL_CANVAS_WIDTH, height: LOGICAL_CANVAS_HEIGHT };

/**
 * Fixed content extent of a bookpage page, in image px (cross-ref `canvas-bookpage-slides` D4a).
 * Bookpage display slides have `panorama == null`, so vertical scroll is sourced from this constant.
 * Bookpage pages are always 1115×1540.
 */
const BOOKPAGE_SIZE: ViewportSize = { width: 1115, height: 1540 };

/**
 * Normal picture / whiteboard / perception background: fills the canvas exactly, no scroll, coords
 * already logical (scale 1). Exports at the logical 1280×720.
 */
export class FixedImageBackground implements SlideBackground {
    readonly fit: BackgroundFit = 'fill';
    coordinateSpace(): CoordinateSpace {
        return { viewport: LOGICAL_SIZE, contentToLogicalScale: 1, fit: this.fit };
    }
    scrollModel(): ScrollableViewport | null {
        return null;
    }
    exportSize(): ViewportSize {
        return LOGICAL_SIZE;
    }
    exportPolicy(): SlideExportPolicy {
        return { multiplier: FIXED_MASTER_MULTIPLIER, uniformLadder: false };
    }
}

/**
 * Panorama background (`slide.panorama`): the image overflows one axis and the viewport pans across
 * it. Scaled uniformly so the non-pan axis fills the canvas (`contain-*`); hotspot coords authored in
 * `panorama.{width,height}` space scale by the same factor the SVG panorama board used
 * (`calculateScaleFactor`: height-fit → `720/height`, width-fit → `1280/width`).
 */
export class PanoramaBackground implements SlideBackground {
    readonly fit: BackgroundFit;
    private readonly content: ViewportSize;

    constructor(private readonly panorama: { direction: ScrollDirection; width: number; height: number }) {
        this.content = { width: panorama.width, height: panorama.height };
        this.fit = panorama.direction === 'Vertical' ? 'contain-width' : 'contain-height';
    }

    coordinateSpace(): CoordinateSpace {
        const scale =
            this.panorama.direction === 'Vertical'
                ? LOGICAL_CANVAS_WIDTH / this.panorama.width
                : LOGICAL_CANVAS_HEIGHT / this.panorama.height;
        return { viewport: LOGICAL_SIZE, contentToLogicalScale: scale, fit: this.fit };
    }

    scrollModel(): ScrollableViewport | null {
        return new ScrollableViewport(this.panorama.direction, this.content, LOGICAL_SIZE);
    }

    exportSize(): ViewportSize {
        const { width: cw, height: ch } = this.content;
        return this.panorama.direction === 'Vertical'
            ? { width: LOGICAL_CANVAS_WIDTH, height: Math.round(ch * (LOGICAL_CANVAS_WIDTH / cw)) }
            : { width: Math.round(cw * (LOGICAL_CANVAS_HEIGHT / ch)), height: LOGICAL_CANVAS_HEIGHT };
    }
    /**
     * Panorama masters export at the panorama's NATIVE content resolution (multiplier undoes the
     * `contentToLogicalScale` normalization) with a uniform ladder — one size for `low`/`med`/`high`,
     * mirroring the content pipeline, which stores panorama variants in exactly one native size
     * (`canvas-slide-favorites` D3 + measurements.md). No upscaling beyond the source. The
     * multiplier is derived from the ROUNDED {@link exportSize} so the scroll axis lands
     * pixel-exact on the native dimension.
     */
    exportPolicy(): SlideExportPolicy {
        const exportSize = this.exportSize();
        // Half-pixel bias: fabric's toCanvasElement assigns `width * multiplier` (float) straight
        // to the canvas element, whose width setter TRUNCATES — the bias makes the scroll axis
        // floor deterministically onto the native dimension (e.g. 3577, not a float-error 3576).
        const multiplier =
            this.panorama.direction === 'Vertical'
                ? (this.panorama.height + 0.5) / exportSize.height
                : (this.panorama.width + 0.5) / exportSize.width;
        return { multiplier, uniformLadder: true };
    }
}

/**
 * Bookpage background: a tall page (always `BOOKPAGE_SIZE`) scrolled vertically, scaled to full
 * width. Coords are logical (scale 1; bookpage carries no panorama). The viewport is fixed 1280×720
 * today — `canvas-bookpage-slides-viewport` will make `coordinateSpace().viewport.height` track the
 * host cell (Decision C9 seam); only this class changes when it lands.
 */
export class BookpageBackground implements SlideBackground {
    readonly fit: BackgroundFit = 'contain-width';

    coordinateSpace(): CoordinateSpace {
        // Seam for canvas-bookpage-slides-viewport: this impl will take `hostSize` and compute
        // viewport.height = clamp(1280·hostH/hostW, 720, ~1768). Fixed 720 today (behavior-preserving).
        return { viewport: LOGICAL_SIZE, contentToLogicalScale: 1, fit: this.fit };
    }

    scrollModel(): ScrollableViewport | null {
        return new ScrollableViewport('Vertical', BOOKPAGE_SIZE, this.coordinateSpace().viewport);
    }

    exportSize(): ViewportSize {
        const { width: cw, height: ch } = BOOKPAGE_SIZE;
        return { width: LOGICAL_CANVAS_WIDTH, height: Math.round(ch * (LOGICAL_CANVAS_WIDTH / cw)) };
    }
    /** Bookpage master/ladder policy — assumed 16:9-like (open question in `canvas-slide-favorites`). */
    exportPolicy(): SlideExportPolicy {
        return { multiplier: FIXED_MASTER_MULTIPLIER, uniformLadder: false };
    }
}

/**
 * Pick the background strategy for a slide. Bookpage is checked first (parity with the legacy
 * `scrollDescriptorFor` order, and Decision C4: a bookpage never loses its background to a stray
 * `panorama`); panorama then refines a fixed background; otherwise fixed. The registry (§4) carries
 * the full `type → recipe` + data-presence refinement; this resolver is the background half of it.
 */
export function resolveBackground(slide: IDisplaySlide): SlideBackground {
    if (slide.type === SlideType.bookpage) return new BookpageBackground();
    if (slide.panorama) return new PanoramaBackground(slide.panorama);
    return new FixedImageBackground();
}
